import type { OrderStatusReason } from '@skupreme/api-contracts/core';
import { Badge, type BadgeTone, Tooltip, cn } from '@skupreme/shared/ui';
import { DateFormat, DateUtility } from '@utility/date';
import upperFirst from 'lodash/upperFirst';
import { splitStatusReason } from '../../lib/status-reason';

export type StatusReasonBadgeProps = {
  reason: OrderStatusReason | null | undefined;
  /** The list row's chip size; the detail card uses the default badge. */
  compact?: boolean;
  className?: string;
};

/* A planner hold has not left yet; a provider notice or a stall waits on the provider. */
const TONES: Record<string, BadgeTone> = {
  planner: 'warning',
  provider: 'caution',
  stall: 'caution',
};

/**
 * The order keeps its status and tab; the badge only says why it waits. It
 * shows the part after the colon (the stage badge beside it names the rest),
 * cut to the space it has; the tooltip has the whole message.
 */
export function StatusReasonBadge({
  reason,
  compact,
  className,
}: StatusReasonBadgeProps) {
  if (!reason?.message) {
    return null;
  }

  const { title, detail } = splitStatusReason(reason.message);
  const label = detail ? upperFirst(detail) : reason.message;
  const since = DateUtility.formatDate(
    reason.since,
    DateFormat.meridianTimeDate,
  );

  return (
    <Tooltip
      placement="bottom start"
      content={
        <span className="sk:flex sk:flex-col sk:gap-1">
          <span>{reason.message}</span>
          <span className="sk:text-fg-tertiary">
            {title} since {since}
            {reason.detail ? ` · ${reason.detail}` : ''}
          </span>
        </span>
      }
    >
      <span
        className={cn(
          'sk:inline-flex sk:min-w-0 sk:max-w-full sk:cursor-default',
          className,
        )}
      >
        <Badge
          tone={TONES[reason.source] ?? 'neutral'}
          className={cn(
            'sk:max-w-full',
            compact && 'sk:text-xs sk:font-normal',
          )}
        >
          <span className="sk:truncate">{label}</span>
        </Badge>
      </span>
    </Tooltip>
  );
}
